<template>
    <div class="w-full space-y-4">
        <h2 class="font-semibold text-xl text-center">{{ $t('views.farmToFarm.selectFarms.title') }}</h2>

        <StepQueryState :is-loading="isLoading" :is-error="isError" @retry="refetch">
            <div class="animal-group-choice flex justify-center overflow-x-auto mb-4" role="radiogroup" :aria-label="$t('views.farmToFarm.selectFarms.title')">
                <button
                    v-for="group of groups"
                    :key="group.animal_group_id"
                    type="button"
                    role="radio"
                    class="mx-2"
                    :class="{ '--active': isGroupActive(group) }"
                    :aria-checked="isGroupActive(group)"
                    @click="onGroupClicked(group)"
                >
                    {{ group.name }}
                </button>
            </div>

            <div class="flex justify-center h-full">
                <div class="max-w-[80%] w-full h-full">
                    <h3 class="text-center pb-2 font-semibold">{{ $t('views.farmToFarm.selectFarms.description', { max: maxFarms }) }}</h3>

                    <CheckboxTree :nodes="nodes" :selected-keys="selectedKeys" :empty-label="$t('views.farmToFarm.selectFarms.noFarms')" @update:selected-keys="applySelection" />

                    <p v-if="atLimit" class="pt-2 text-center text-sm text-amber-600">
                        {{ $t('views.farmToFarm.selectFarms.limitReached', { max: maxFarms }) }}
                    </p>
                </div>
            </div>
        </StepQueryState>

        <div class="flex justify-center">
            <CustomButton :disabled="!canContinue" class="max-w-32" @click="$emit('next')">
                {{ $t('views.reports.nextButtonLabel') }}
            </CustomButton>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import CheckboxTree from '@/components/common/CheckboxTree.vue';
import CustomButton from '@/components/ui/CustomButton.vue';
import { store } from '@/plugins/store';
import { useFarmToFarmLocations } from '@/plugins/store/actions/queries/farm-to-farm';
import { CheckboxTreeNode, FARM_TO_FARM_MAX_FARMS, FarmToFarmAnimalGroup, FarmToFarmViewState } from '@/types';
import StepQueryState from '../StepQueryState.vue';

/**
 * Step one: pick an animal group, then farms beneath it.
 *
 * Laid out like the farms/batches step on /reports - a centred pair of pill buttons above a
 * bordered, scrollable list - because it is the same kind of choice and users move between the
 * two reports.
 *
 * The group is single-select and switching it clears the farms, exactly as switching tab does on
 * /reports. The row sets differ per group - a weaner farm has no carcass weight or meat
 * percentage - so a mixed selection has no coherent table to render.
 *
 * Both groups are always offered, even when a directory holds no farms of one kind: the empty
 * list says so, whereas a missing button would look like a broken screen.
 */
export default defineComponent({
    components: { CheckboxTree, CustomButton, StepQueryState },
    props: {
        state: {
            type: Object as PropType<FarmToFarmViewState>,
            required: true,
        },
    },
    emits: ['next', 'reset', 'update:state'],
    setup() {
        // Not destructured as `data`: that name collides with the Options API data() option.
        const { data: locations, isLoading, isError, refetch } = useFarmToFarmLocations();
        return { locations, isLoading, isError, refetch };
    },
    data() {
        return {
            maxFarms: FARM_TO_FARM_MAX_FARMS,
        };
    },
    computed: {
        /**
         * The shared wizard state, written through rather than assigned to directly. The report
         * steps on /reports use the same indirection, and it keeps every mutation in one shape.
         */
        computedState: {
            get(): FarmToFarmViewState {
                return this.$props.state;
            },
            set(newValue: FarmToFarmViewState) {
                this.$emit('update:state', newValue);
            },
        },
        groups(): FarmToFarmAnimalGroup[] {
            return this.locations ?? [];
        },
        activeGroup(): FarmToFarmAnimalGroup | null {
            return this.groups.find((group) => group.animal_group_id === this.computedState.animalGroupId) ?? null;
        },
        /**
         * A flat list - farms have no sub-rows here. The cap is expressed as `disabled` rather than
         * enforced on click, so the tree cannot hand back an over-long selection in the first place.
         */
        nodes(): CheckboxTreeNode[] {
            return (this.activeGroup?.farms ?? []).map((farm) => ({
                key: String(farm.id),
                label: farm.name,
                // Only the cap disables a farm, and never one already selected - otherwise the user
                // could not undo their tenth pick.
                disabled: this.atLimit && !this.computedState.selectedFarmIds.includes(farm.id),
            }));
        },
        selectedKeys(): string[] {
            return this.computedState.selectedFarmIds.map((id) => String(id));
        },
        atLimit(): boolean {
            return this.computedState.selectedFarmIds.length >= this.maxFarms;
        },
        canContinue(): boolean {
            return this.computedState.animalGroupId !== null && this.computedState.selectedFarmIds.length > 0;
        },
    },
    watch: {
        groups: {
            immediate: true,
            handler(groups: FarmToFarmAnimalGroup[]) {
                if (!groups.length) return;

                // Coming back from the period step the group is already chosen; leave it alone. This
                // is also what keeps the seeding below to the first load: once anything is selected,
                // neither a return trip nor a refetched tree re-seeds over the user's choice.
                if (groups.some((group) => group.animal_group_id === this.computedState.animalGroupId)) return;

                // Otherwise land on the user's usual kind of pig, then on anything selectable.
                this.selectGroup(this.preferredGroup(groups) ?? groups.find((group) => group.farms.length) ?? groups[0]);
            },
        },
    },
    methods: {
        /**
         * The group matching the app-wide slaughter-pigs/weaners preference, so the wizard opens on
         * the same kind of pig as PigTypePicker rather than on whichever group happens to sort
         * first. Null when the directory holds no barns of that kind, leaving the caller to fall
         * back.
         */
        preferredGroup(groups: FarmToFarmAnimalGroup[]): FarmToFarmAnimalGroup | null {
            const wantWeaners = store.getters.getIsWeaners;
            const groupIds = new Set((store.state.barns ?? []).filter((barn) => barn.is_weaners === wantWeaners).map((barn) => barn.animal_group_id));

            return groups.find((group) => groupIds.has(group.animal_group_id) && group.farms.length) ?? null;
        },
        isGroupActive(group: FarmToFarmAnimalGroup): boolean {
            return this.computedState.animalGroupId === group.animal_group_id;
        },
        selectGroup(group: FarmToFarmAnimalGroup) {
            Object.assign(this.computedState, {
                animalGroupId: group.animal_group_id,
                selectedFarmIds: [],
            });
        },
        onGroupClicked(group: FarmToFarmAnimalGroup) {
            if (this.isGroupActive(group)) return;
            this.selectGroup(group);
        },
        applySelection(keys: string[]) {
            Object.assign(this.computedState, { selectedFarmIds: keys.map((key) => Number(key)) });
        },
    },
});
</script>

<style scoped>
/* The pill treatment from the farms/batches tabs on /reports, so the two pickers read as one family. */
.animal-group-choice button {
    border-width: 1px;
    border-radius: 0.5rem;
    @apply border-opacity-20;
    padding: 0.5rem 1.25rem 0.5rem 1.25rem;
    font-weight: 600;
}

.animal-group-choice button.--active {
    @apply bg-primary-800;
    @apply border-primary-700;
    color: white;
}
</style>
